import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminAfriexPerson,
  AdminPaginatedEnvelope,
  AfriexQueryParams,
  AfriexRoles,
  AfriexUserBalances,
  DeactivateUserBody,
} from '@/types';
import type {AfriexDispute, AfriexDisputeCount} from '@/types/AfriexDispute';
import type {AfriexLog} from '@/types/AfriexLog';
import type {
  AfriexSecurityNote,
  AfriexSecurityNoteRequest,
  AfriexSecurityNotesQueryParams,
} from '@/types/Notes';
import type {UserUpdateBody} from '@/types/UserUpdateBody';

const columns = [
  'name',
  'username',
  'currentCountry',
  'currentEmail',
  'currentPhone',
  'kyc',
  'isPhoneVerified',
  'isSecurityFlagged',
  'createdAt',
  'externalAccounts',
  'rewardInfo',
  'devices',
];

const toLogRow = (person: AdminAfriexPerson): AfriexLog => ({
  id: person.id,
  userId: person.id ?? '',
  sessionId: '',
  logs: [],
});

export const userService = {
  getUsers: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AdminAfriexPerson[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/users?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AdminAfriexPerson[]>>(url);
    return response.data;
  },
  getUser: async (userId: string): Promise<AdminAfriexPerson> => {
    const response = await axios.get<AdminAfriexPerson>(
      `/v2/admin/users/${userId}`,
    );
    return response.data;
  },
  getUserByBankAccountNumber: async (
    bankAccountNumber: string,
  ): Promise<AdminAfriexPerson> => {
    const params = {bankAccountNumber};
    const urlParams = new URLSearchParams(params).toString();
    const response = await axios.get<AdminAfriexPerson>(
      `/v2/admin/users/payment-method?${urlParams}`,
    );
    return response.data;
  },
  updateUser: async (
    userId: string,
    body: Partial<UserUpdateBody>,
  ): Promise<unknown> => {
    const response = await axios.patch<unknown>(
      `/v2/admin/users/${userId}`,
      body,
    );
    return response.data;
  },
  searchUser: async (searchTerm: string): Promise<AdminAfriexPerson> => {
    const url = `/v2/admin/users/search?${toQueryString({searchTerm, columns})}`;
    const response = await axios.get<AdminAfriexPerson>(url);
    return response.data;
  },
  searchLog: async (searchTerm: string): Promise<AfriexLog | null> => {
    const url = `/v2/admin/logs/search?${toQueryString({searchTerm, columns})}`;
    // The endpoint returns the matching person (`id`), not a log doc, so the
    // User ID column needs `userId` filled from it. A no-match body is null.
    const response = await axios.get<AdminAfriexPerson | null>(url);
    return response.data && toLogRow(response.data);
  },
  searchAutoComplete: async (
    searchTerm: string,
  ): Promise<AdminAfriexPerson[]> => {
    let term = searchTerm;

    if (term.includes('@')) {
      term = term.replace(/@/g, '');
    }

    const searchParams = toQueryString({
      searchTerm: term?.trim()?.toString(),
      columns,
    });
    const url = `/v2/admin/users/autocomplete?${searchParams}`;
    const response = await axios.get<AdminAfriexPerson[]>(url);

    return response.data || [];
  },
  searchLogAutoComplete: async (searchTerm: string): Promise<AfriexLog[]> => {
    const searchParams = toQueryString({
      searchTerm: searchTerm?.trim()?.toString(),
      columns,
    });
    const url = `/v2/admin/logs/autocomplete?${searchParams}`;
    const response = await axios.get<AdminAfriexPerson[]>(url);
    return (response.data || []).map(toLogRow);
  },
  getUserWalletBalance: async (
    userId: string,
    signal?: AbortSignal,
  ): Promise<AfriexUserBalances> => {
    const response = await axios.get<AfriexUserBalances>(
      `/v2/admin/users/balance/${userId}`,
      {signal},
    );
    const balances = response.data;
    const filteredBalances = Object.entries(
      balances,
    ).reduce<AfriexUserBalances>((acc, [key, value]) => {
      if (value !== 0) {
        acc[key] = value;
      }
      return acc;
    }, {});
    return filteredBalances;
  },
  getUserSecurityNotes: async (
    userId: string,
    filters: AfriexSecurityNotesQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexSecurityNote[]>> => {
    const urlParams = toQueryString({page: 0, limit: 6, ...filters});
    const url = `/v2/admin/security-notes/user/${userId}?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexSecurityNote[]>>(url);
    return response.data;
  },
  createSecurityNote: async (
    note: AfriexSecurityNoteRequest,
  ): Promise<AfriexSecurityNote> => {
    const response = await axios.post<AfriexSecurityNote>(
      `/v2/admin/security-notes`,
      note,
    );
    return response.data;
  },
  blockUser: async (
    userId: string,
    isBlocked: boolean,
    note?: string,
    reason?: string,
    unblockReason?: string,
  ): Promise<unknown> => {
    const response = await axios.post<unknown>(
      `/v2/admin/users/block/${userId}`,
      {
        isSecurityEnabled: !isBlocked,
        note,
        reason: !isBlocked ? reason : undefined,
        unblockReason: isBlocked ? unblockReason : undefined,
      },
    );
    return response.data;
  },
  getUserLogs: async (
    userId: string,
    {limit = 10, page = 0}: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexLog[]>> => {
    const url = `/v2/admin/logs/${userId}?${toQueryString({page, limit})}`;
    const response = await axios.get<AdminPaginatedEnvelope<AfriexLog[]>>(url);
    return {
      data: response.data?.data,
      total: response.data?.total,
      page,
    };
  },
  getDisputes: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexDispute[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/disputes?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexDispute[]>>(url);
    return response.data;
  },
  updateDispute: async (
    disputeId: string,
    updateData: {
      status?: string;
      internalData?: {
        providerStatus?: string;
        providerReason?: string;
      };
      notes?: {
        note: string;
        createdAt: Date;
        updatedAt: Date;
      }[];
    },
  ): Promise<AfriexDispute> => {
    const url = `/v2/admin/disputes/${disputeId}`;
    const response = await axios.patch<AfriexDispute>(url, updateData);
    return response?.data;
  },
  countDisputes: async (): Promise<AfriexDisputeCount> => {
    const url = '/v2/admin/disputes/count';
    const response = await axios.get<AfriexDisputeCount>(url);
    return response.data;
  },
  deactivateUser: async (
    userId: string,
    body: DeactivateUserBody,
  ): Promise<unknown> => {
    const response = await axios.patch<unknown>(
      `/v2/admin/users/deactivate/${userId}`,
      body,
    );
    return response.data;
  },
  updateUserRole: async (
    userId: string,
    afriexRole: AfriexRoles,
  ): Promise<unknown> => {
    const response = await axios.patch<unknown>(
      `/v2/admin/users/${userId}/role`,
      {
        role: afriexRole,
      },
    );
    return response.data;
  },
};
